import { useState, type JSX } from 'react';
import type { CommonProps } from '../../types';
import { tv, type VariantProps } from 'tailwind-variants';
import { FormInputGroup } from '../../molecules';

export interface DropdownProps
    extends CommonProps,
        VariantProps<typeof DropdownStyles> {
    options: { key: string; value: string }[];
    onChange?: (value: string) => void;
    defaultValue?: string;
    label: string;
    name?: string;
    id: string;
    required?: boolean;
    emptyTitle?: string;
    backgroundColor?: string;
    labelHidden?: boolean;
    isDisabled?: boolean;
    hasErrors?: boolean;
    wrapperClassName?: string;
}

export const DropdownStyles = tv({
    slots: {
        label: '',
        select: 'bg-gray-500 appearance-none focus h-11 w-full rounded-full empty:text-danger py-[9px] pl-4 pr-2 text-white placeholder-gray-100 focus-visible:rounded-full disabled:bg-gray-300 disabled:text-gray-100',
        selectWrapper:
            'relative after:content-[""] after:border-t-2 after:border-r-2 after:rotate-[135deg] after:w-2 after:h-2 after:block after:absolute after:top-4 after:right-4 after:pointer-events-none after:text-xs text-gray-100',
    },
    variants: {
        required: {
            true: {
                label: "after:content-['*']",
            },
        },
        labelHidden: {
            true: {
                label: 'opacity-0 absolute',
            },
        },
        hasErrors: {
            true: {
                select: 'border-danger-500 border',
                label: 'text-danger-500',
            },
            false: {},
        },
        isEmpty: {
            true: {
                select: 'text-gray-100',
            },
        },
        layout: {
            next: {
                label: 'text-gray-100 text-sm ml-2 inline-block',
            },
            mybeat: {
                select: 'bg-transparent border border-gray-100',
                label: '-mb-2 pl-5 opacity-70',
            },
        },
    },
    defaultVariants: {
        layout: 'next',
    },
});

export const Dropdown = ({
    className,
    wrapperClassName,
    options,
    onChange,
    required,
    emptyTitle,
    isDisabled,
    defaultValue = '',
    label,
    id,
    name,
    layout,
    labelHidden,
    hasErrors,
    testId = 'dropdown',
}: DropdownProps): JSX.Element => {
    const {
        label: labelStyle,
        select,
        selectWrapper,
    } = DropdownStyles({ hasErrors });
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    return (
        <FormInputGroup className={wrapperClassName}>
            <label
                className={labelStyle({ required, labelHidden, layout })}
                htmlFor={id}
            >
                {label}
            </label>

            <div className={selectWrapper()}>
                <select
                    data-testid={testId}
                    id={id}
                    name={name ?? id}
                    required={required}
                    onChange={(e) => {
                        setSelectedValue(e.target.value);
                        onChange?.(e.target.value);
                    }}
                    className={select({
                        isEmpty: selectedValue === '',
                        layout,
                        className,
                    })}
                    defaultValue={defaultValue}
                    disabled={isDisabled}
                >
                    <option value="" disabled>
                        {emptyTitle}
                        {required && labelHidden && '*'}
                    </option>

                    {options.map((item, index) => (
                        <option key={`${item.key}-${index}`} value={item.key}>
                            {item.value}
                        </option>
                    ))}
                </select>
            </div>
        </FormInputGroup>
    );
};
